import {
  PROMOTION_SCHEDULE_LIST_DEFAULT_SORT_BY,
  PROMOTION_SCHEDULE_LIST_DEFAULT_SORT_ORDER,
  PROMOTION_SCHEDULE_LIST_SORT_FIELDS,
  PromotionScheduleListFilter,
  PromotionSchedulesListQuery,
} from '../../../../lib/api/promotionSchedule';
import { ListState, useListState } from '../../../../components/list';
import { EMPTY_SCHEDULE_LIST_FILTER, parseScheduleFilter, serializeScheduleFilter } from '../utils/scheduleListQuery';

export type ScheduleListState = ListState<PromotionScheduleListFilter> & {
  /** 서버 조회 파라미터 — 빈 값은 빼서 보낸다 */
  listQuery: PromotionSchedulesListQuery;
};

/**
 * 프로모션 리스트 화면 상태 (AMZUWEB-682).
 *
 * 공통 `useListState`(URL 쿼리 정본)에 **프로모션 필터의 해석 규칙**만 얹는다.
 * 파싱은 어떤 입력에도 던지지 않는다 — 이 화면의 출발점이 "필터 걸린 링크를 주고받는 것" 이라
 * 남이 보낸 링크가 깨져 있어도 빈 화면이 되면 안 된다.
 */
export const useScheduleListState = (): ScheduleListState => {
  const state = useListState<PromotionScheduleListFilter>({
    defaultFilter: EMPTY_SCHEDULE_LIST_FILTER,
    parse: parseScheduleFilter,
    serialize: serializeScheduleFilter,
    sortableFields: PROMOTION_SCHEDULE_LIST_SORT_FIELDS,
    defaultSortBy: PROMOTION_SCHEDULE_LIST_DEFAULT_SORT_BY,
    defaultSortOrder: PROMOTION_SCHEDULE_LIST_DEFAULT_SORT_ORDER,
  });

  const { page, pageSize, sortBy, sortOrder, query } = state;

  const listQuery: PromotionSchedulesListQuery = {
    ...serializeToFilter(query),
    page,
    pageSize,
    sortBy: sortBy as PromotionSchedulesListQuery['sortBy'],
    sortOrder,
  };

  return { ...state, listQuery };
};

/** `query` 에서 페이지·정렬을 떼고 **값이 있는 필터만** 남긴다 (쿼리스트링을 짧게 유지) */
const serializeToFilter = (query: PromotionScheduleListFilter): PromotionScheduleListFilter => {
  const filter: PromotionScheduleListFilter = {};

  if (query.from) filter.from = query.from;
  if (query.to) filter.to = query.to;
  if (query.tracks?.length) filter.tracks = query.tracks;
  if (query.statuses?.length) filter.statuses = query.statuses;
  if (query.types?.length) filter.types = query.types;
  if (query.adminIds?.length) filter.adminIds = query.adminIds;
  if (query.hasEvent !== undefined) filter.hasEvent = query.hasEvent;
  if (query.hasCoupon !== undefined) filter.hasCoupon = query.hasCoupon;
  if (query.keyword) filter.keyword = query.keyword;

  return filter;
};
